/**
 * useTasks — real-time task feed for the dedicated Plate page.
 *
 * Subscribes to all tenant tasks (bounded), then filters by scope (My Plate vs
 * Team, optionally one person) and pre-groups them by status / priority /
 * entity / assignee for the List + Board views. Grouping is memoised.
 */

import { useState, useEffect, useMemo } from 'react';
import { subscribeTasksForScope } from '@/services/todo.service';
import type { TodoItem, TaskStatus, TaskPriority, TaskEntityKind } from '@/types/schema';
import { STATUS_ORDER, PRIORITY_ORDER } from '@/config/entityThemes';

export type TaskScope = 'mine' | 'team';

interface UseTasksParams {
  scope: TaskScope;
  userId: string;
  tenantId: string;
  /** Team view: restrict to a single person's tasks. */
  personId?: string;
}

export interface AssigneeGroup {
  userId: string;
  userName: string;
  tasks: TodoItem[];
}

interface UseTasksReturn {
  /** All tenant tasks (unscoped) — used to resolve a task by id (deep-link /
   *  subtasks) regardless of the active scope filter. */
  all: TodoItem[];
  tasks: TodoItem[];
  byStatus: Record<TaskStatus, TodoItem[]>;
  byPriority: Record<TaskPriority, TodoItem[]>;
  byEntity: Record<TaskEntityKind, TodoItem[]>;
  byAssignee: AssigneeGroup[];
  loading: boolean;
}

export function useTasks({ scope, userId, tenantId, personId }: UseTasksParams): UseTasksReturn {
  const [all, setAll] = useState<TodoItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    const unsubscribe = subscribeTasksForScope(tenantId, (tasks) => {
      setAll(tasks);
      setLoading(false);
    });
    return unsubscribe;
  }, [tenantId]);

  const tasks = useMemo(() => {
    if (scope === 'mine') {
      return all.filter((t) => t.assigneeIds?.includes(userId));
    }
    if (personId) {
      return all.filter((t) => t.assigneeIds?.includes(personId));
    }
    return all;
  }, [all, scope, userId, personId]);

  const byStatus = useMemo(() => {
    const groups = { todo: [], in_progress: [], blocked: [], done: [] } as Record<
      TaskStatus,
      TodoItem[]
    >;
    tasks.forEach((t) => groups[(t.status ?? 'todo') as TaskStatus]?.push(t));
    STATUS_ORDER.forEach((s) => groups[s].sort(sortInColumn));
    return groups;
  }, [tasks]);

  const byPriority = useMemo(() => {
    const groups = { urgent: [], high: [], normal: [], low: [] } as Record<
      TaskPriority,
      TodoItem[]
    >;
    tasks.forEach((t) => groups[(t.priority ?? 'normal') as TaskPriority]?.push(t));
    PRIORITY_ORDER.forEach((p) => groups[p].sort(sortInColumn));
    return groups;
  }, [tasks]);

  const byEntity = useMemo(() => {
    const groups: Record<string, TodoItem[]> = {};
    tasks.forEach((t) => {
      const k = t.entityKind ?? 'general';
      (groups[k] ??= []).push(t);
    });
    return groups as Record<TaskEntityKind, TodoItem[]>;
  }, [tasks]);

  const byAssignee = useMemo(() => {
    const map = new Map<string, AssigneeGroup>();
    tasks.forEach((t) => {
      (t.assignees ?? []).forEach((a) => {
        let g = map.get(a.userId);
        if (!g) {
          g = { userId: a.userId, userName: a.userName, tasks: [] };
          map.set(a.userId, g);
        }
        g.tasks.push(t);
      });
    });
    const groups = [...map.values()];
    groups.forEach((g) => g.tasks.sort(sortInColumn));
    groups.sort((a, b) => b.tasks.length - a.tasks.length);
    return groups;
  }, [tasks]);

  return { all, tasks, byStatus, byPriority, byEntity, byAssignee, loading };
}

/** Sort within a board column: open before done, then priority, then manual order. */
function sortInColumn(a: TodoItem, b: TodoItem): number {
  const soA = a.statusOrder ?? a.order ?? 0;
  const soB = b.statusOrder ?? b.order ?? 0;
  if (soA !== soB) return soA - soB;
  return (a.createdAt || '').localeCompare(b.createdAt || '');
}
